import { Stage, useEnvironment } from "@react-three/drei";
import { useControls, folder } from "leva";
import { usePerfManager } from "@/features/performance/PerfManager";
import { memo, useEffect, useRef } from "react";
import { RESOURCES } from "@/data/resources";
import { useExperienceStore } from "@/stores/useExperienceStore";
import { Group } from "three";

export type Presets = {
  lightPreset: "rembrandt" | "portrait" | "upfront" | "soft";
  envPreset: "sunset" | "dawn" | "night" | "city" | "studio";
  shadows: "contact" | "accumulative";
};

const SceneStage = memo(function SceneStage({children}: { children: React.ReactNode | React.ReactNode[] }) 
{
  const folderName = "Scene";

  // 2. Light Controls
  const [lightConfig, setLight] = useControls(
    folderName,
    () => ({
      lights: folder({
        lightIntensity: { value: 1.5, min: 0, max: 5 },
        lightPreset: {
          value: "rembrandt",
          options: ["rembrandt", "portrait", "upfront", "soft"],
        },
      }),
    }),
    []
  );

  // 2. Environment Controls
  const [envConfig, setEnv] = useControls(
    folderName,
    () => ({
      environment: folder(
        {
          preset: {
            value: "city",
            options: ["sunset", "dawn", "night", "city", "studio"],
            label: "Preset",
          },

          rotation: {
            value: { x: 0, y: 0, z: 0 },
            min: 0,
            max: 360,
            step: 1,
            label: "Map Rotation",
          },
          envIntensity: { value: 1, min: 0, max: 5, label: "Env Intensity" },

          background: folder({
            showBackground: { value: false, label: "show" },
            blur: { value: 0, min: 0, max: 2 },
          }),
        },
        { collapsed: true }
      ),
    }),
    { collapsed: true },
    []
  );

  // 3. Shadows Controls
  const [shadowConfig, setShadow] = useControls(
    folderName,
    () => ({
      shadows: folder(
        {
          type: {
            value: "accumulative",
            options: ["contact", "accumulative", "none"],
          },
          bias: {
            value: -0.0001,
            min: -0.01,
            max: 0.01,
            render: (get) => get("Scene.shadows.type") !== "none",
          },
          normalBias: {
            value: 0,
            min: 0,
            max: 1,
            render: (get) => get("Scene.shadows.type") !== "none",
          },
          offset: {
            value: 0,
            min: -10,
            max: 10,
            render: (get) => get("Scene.shadows.type") !== "none",
          },
          size: {
            value: 512,
            min: 64,
            max: 2048,
            step: 64,
            render: (get) => get("Scene.shadows.type") !== "none",
          },

          contact: folder(
            {
              contactOpacity: { value: 0.7, min: 0, max: 1 },
              contactBlur: { value: 2, min: 0, max: 10 },
            },
            {
              collapsed: true,
            }
          ),

          accumulative: folder(
            {
              framesInfinite: false, 
              frames: { value: 100, min: 1, max: 100, step: 1 },
              blend: { value: 100, min: 0, max: 1000 },
              limit: { value: 1000, min: 1, max: 1000, step: 1 },
              scale: { value: 15, min: 1, max: 50 },
              temporal: { value: true },
              opacity: { value: 1, min: 0, max: 1 },
              alphaTest: { value: 0.23, min: 0, max: 1 },
              color: { value: "#49556B" },
              colorBlend: { value: 2, min: 0, max: 2 },
              resolution: { value: 1024, min: 64, max: 2048, step: 64 },
            },
            {
              collapsed: true,
            }
          ),
        },
        { collapsed: true }
      ),
    }),
    { collapsed: true }
  );

  // Performance
  // useEffect(() => {
  //   if (!isActive) return;
  //   // Sync Shadows (Nested in 'shadows' folder)
  //   setShadow({
  //     type: settings.scene.shadowType,
  //     size: settings.scene.shadowResolution,
  //     contactBlur: settings.scene.contactBlur,
  //     frames: settings.scene.shadowFrames,
  //     resolution: settings.scene.shadowResolution,
  //   });
  // }, [settings, isActive, setShadow]);

  const envMap = useEnvironment({ files: RESOURCES.environment.path }); //prettier-ignore

  // 1.
  return (
    <Stage
      // Stage Props
      adjustCamera={false}
      preset={lightConfig.lightPreset as Presets["lightPreset"]}
      intensity={lightConfig.lightIntensity}
      // Environment Props
      environment={{
        map: envMap,
        //preset: envConfig.preset as Presets["envPreset"],
        background: envConfig.showBackground,
        blur: envConfig.blur,
        backgroundIntensity: envConfig.envIntensity,
        environmentIntensity: envConfig.envIntensity,
        files: undefined, // Ensure preset is used
      }}
      // Shadow Props (Merged)
      shadows={{
        type: shadowConfig.type as Presets["shadows"],

        // Shared
        bias: shadowConfig.bias,
        normalBias: shadowConfig.normalBias,
        offset: shadowConfig.offset,
        mapSize: shadowConfig.size,

        // Contact specific
        opacity: shadowConfig.contactOpacity,
        blur: shadowConfig.contactBlur,

        // Accumulative specific
        frames: shadowConfig.framesInfinite ? Infinity : shadowConfig.frames,
        blend: shadowConfig.blend,
        limit: shadowConfig.limit, // limit if frames are infinite
        scale: shadowConfig.scale,
        temporal: shadowConfig.temporal,
        alphaTest: shadowConfig.alphaTest,
        color: shadowConfig.color,
        colorBlend: shadowConfig.colorBlend,
        resolution: shadowConfig.resolution,
      }}
    >
      {children}
    </Stage>
  );
});

export function Scene({
  children,
}: {
  children: React.ReactNode | React.ReactNode[];
}) {

  /** Wrapper to ensure scene is positioned and repositioned without updating shadows.
   *  This is done from the camera view, so its put inside the store.
   */
  
  const { y } = useExperienceStore((s) => s.scenePosition)
  return (
    <group position={[0, y, 0]}>
      <SceneStage>{children}</SceneStage>
    </group>
  );
}
